import React, { useEffect, useMemo } from 'react';
import ProjectStartup from './components/ProjectStartup';
import WorkspaceShell from './components/Workspace/WorkspaceShell';
import { WorkspaceContext } from './components/Workspace/WorkspaceContext';
import { useCopilot } from './components/Copilot/CopilotProvider';
import { useModal } from './components/ModalSystem';
import { isTemplateMode } from './services/lcaCalculator';
import ipc from './services/ipc';
import { registerBridge } from './services/agent/actionBridge';
import { useProjectStore } from './store/projectStore';
import './App.css';
import { getActiveImpactSource, getImpactLink } from './services/impactSource';

// =============================================================================
// App — Root component for LiTeCAB desktop
//
// Architecture (v3 — template mode + central store):
//   • Templates carry baked-in TRACI v2.1 scores and cost_per_unit
//   • LCA/TEA = pure JS arithmetic (no Python, no Brightway)
//   • All project/results/workspace state + actions live in the Zustand store
//     (src/store/projectStore.js). App is a thin orchestrator: it injects the
//     modal helpers, wires the Electron menu / web-hotkey IPC events to store
//     actions, registers the agent bridge against the store, and renders.
//   • Focus-mode workspace (Build / Inventory / Results / Compare) reads the
//     store through WorkspaceContext, which App builds below.
// =============================================================================

function App() {
  const { showAlert, showConfirm } = useModal();
  const { open: copilotOpen, docked: copilotDocked, setDocked } = useCopilot();

  // App is the orchestrator: subscribe to the whole store and rebuild the
  // WorkspaceContext value each render (same render cadence as before the store
  // extraction — every setState used to re-render App too).
  const s = useProjectStore();

  // Give the store the React-bound modal helpers (stable callbacks → runs once).
  useEffect(() => {
    useProjectStore.getState().setModal({ showAlert, showConfirm });
  }, [showAlert, showConfirm]);

  // Fetch build info once so demo builds can hide licensed-only UI.
  useEffect(() => {
    useProjectStore.getState().initBuildInfo();
  }, []);

  // Dock the copilot as a fixed right panel while inside a project; float it on
  // the welcome screen. Driven off a stable boolean so per-edit `project`
  // changes don't re-open a dock the user collapsed.
  const inProject = !s.showStartup && !!s.project;
  useEffect(() => { setDocked(inProject); }, [inProject, setDocked]);

  // -- Menu event wiring (Electron OS menu → store actions) --------------------
  useEffect(() => {
    const st = () => useProjectStore.getState();
    const handlers = {
      'menu-new-project':    () => st().newProject(),
      'menu-open-project':   () => st().openProject(),
      'menu-save-project':   () => st().saveProject(),
      'menu-run-lca':        () => st().runLCA(),
      'menu-run-tea':        () => st().runTEA(),
      'menu-run-all':        () => st().runAll(),
      'menu-export-results': () => st().exportResults(),
      'show-status':         (msg) => st().setStatusMessage(msg),
    };
    const cleanups = Object.entries(handlers).map(([channel, fn]) => ipc.on(channel, fn));
    return () => cleanups.forEach((c) => c());
  }, []);

  // -- Web-only hotkey rebinding ----------------------------------------------
  // In Electron, OS menu accelerators (Cmd+N, Cmd+S, …) fire `menu-*` IPC
  // events. There is no OS menu in the browser, so we map the same shortcuts to
  // the same store actions via a global keydown listener.
  useEffect(() => {
    if (process.env.REACT_APP_WEB !== '1') return undefined;

    const onKey = (e) => {
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;

      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      const st = useProjectStore.getState();

      let handled = true;
      if (e.shiftKey && k === 'r')      st.runAll();
      else if (k === 'n')               st.newProject();
      else if (k === 'o')               st.openProject();
      else if (k === 's')               st.saveProject();
      else if (k === 'e')               st.exportResults();
      else if (k === 'l')               st.runLCA();
      else if (k === 't')               st.runTEA();
      else handled = false;

      if (handled) e.preventDefault();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // -- AI copilot action bridge ------------------------------------------------
  // The store's getState() is always current, so we register the bridge once on
  // mount (no more re-registering every render or mirroring state into refs).
  // Actions adapt the agent's argument shapes onto store actions.
  useEffect(() => {
    registerBridge({
      getState: () => useProjectStore.getState(),
      actions: {
        // -- undo (copilot writes are snapshotted before they apply) --
        snapshot: (args) => useProjectStore.getState().snapshotForUndo(args),
        undo: () => useProjectStore.getState().undo(),
        // -- reads / navigation --
        selectProcess: ({ processId } = {}) => {
          const st = useProjectStore.getState();
          const proc = processId ? st.project?.processes?.[processId] || null : null;
          st.selectProcess(proc);
          return { selected: proc?.id || null };
        },
        setView: (args) => useProjectStore.getState().setView(args),
        // -- project lifecycle --
        loadProject: ({ project: p, source } = {}) => useProjectStore.getState().projectSelected(p, source || 'template'),
        saveProject: () => useProjectStore.getState().saveProject(),
        goToStartup: () => useProjectStore.getState().goToStartup(),
        // -- process edits --
        addProcess: (args) => useProjectStore.getState().addProcess(args || {}),
        updateProcess: ({ processId, updates } = {}) => useProjectStore.getState().updateProcess(processId, updates),
        renameProcess: ({ processId, name } = {}) => useProjectStore.getState().renameProcess(processId, name),
        updateProjectTable: ({ project: p } = {}) => useProjectStore.getState().updateProjectTable(p),
        setScalingFactor: ({ value } = {}) => useProjectStore.getState().setScalingFactor(value),
        setFuBasis: ({ basis } = {}) => useProjectStore.getState().setFuBasis(basis),
        setEnergyDensity: ({ value } = {}) => useProjectStore.getState().setEnergyDensity(value),
        // -- analysis --
        runLCA: () => useProjectStore.getState().runLCA(),
        runTEA: () => useProjectStore.getState().runTEA(),
        runCircularity: () => useProjectStore.getState().runCircularity(),
        runAll: () => useProjectStore.getState().runAll(),
        exportResults: () => useProjectStore.getState().exportResults(),
      },
    });
  }, []);

  // -- Derived data ------------------------------------------------------------

  // null = no process selected → Inventory shows the full project view.
  const activeProcess = useMemo(() => {
    if (!s.project?.processes) return null;
    return s.selectedProcess || null;
  }, [s.project, s.selectedProcess]);

  // Reset the spec-name editor when the selected process changes.
  useEffect(() => {
    useProjectStore.getState().setEditingSpecName(false);
  }, [s.selectedProcess?.id]);

  // Pre-compute merged inventory (all processes combined) for the Inventory view.
  const mergedInventory = useMemo(() => {
    if (!s.project?.processes) return { inputs: [], outputs: [] };
    const activeImpactSource = getActiveImpactSource(s.project);
    const inputMap = {};
    const outputMap = {};
    Object.entries(s.project.processes).forEach(([, proc]) => {
      const procName = (proc.name || proc.process_name || proc.id || 'Process').replace(/_/g, ' ');
      const inputs = Array.isArray(proc.lci_inputs) ? proc.lci_inputs
        : (typeof proc.lci_inputs === 'string' ? (() => { try { return JSON.parse(proc.lci_inputs); } catch { return []; } })() : []);
      inputs.forEach((inp) => {
        const key = `${inp.name || 'Unnamed'}||${inp.unit || ''}`;
        const qty = inp.quantity || 0;
        const costPer = typeof inp.cost_per_unit === 'number' ? inp.cost_per_unit : null;
        let row = inputMap[key];
        if (!row) {
          row = inputMap[key] = {
            name: inp.name || 'Unnamed', unit: inp.unit || '', quantity: 0,
            cost_per_unit: null, cost_unit: inp.cost_unit || '', cost_source: inp.cost_source || '',
            activity_name: '', reference_product: '', location: '',
            hasScores: false, totalCost: 0, processes: new Set(),
          };
        }
        row.quantity += qty;
        if (costPer != null) {
          if (row.cost_per_unit == null) { row.cost_per_unit = costPer; row.cost_unit = inp.cost_unit || row.cost_unit; }
          row.totalCost += qty * costPer;
        }
        // Linkage under the project's active dataset only: in GREET mode an
        // input with no GREET equivalent shows as unlinked, not as its ecoinvent row.
        const link = getImpactLink(inp, activeImpactSource);
        if (link) {
          row.hasScores = true;
          if (!row.activity_name && link.activity_name) row.activity_name = link.activity_name;
          if (!row.reference_product && link.reference_product) row.reference_product = link.reference_product;
          if (!row.location && link.location) row.location = link.location;
        }
        if (!row.cost_source && inp.cost_source) row.cost_source = inp.cost_source;
        row.processes.add(procName);
      });
      const isTerminal = !proc.next_process_ids || proc.next_process_ids.length === 0;
      if (isTerminal) {
        (proc.products || proc.output_materials || []).forEach((out) => {
          const name = (out.material || out.name || 'Unnamed').replace(/_/g, ' ');
          const key = `${name}||${out.unit || ''}`;
          if (!outputMap[key]) { outputMap[key] = { name, quantity: 0, unit: out.unit || '', price_per_unit: null }; }
          outputMap[key].quantity += (out.quantity || 0);
          if (outputMap[key].price_per_unit == null && typeof out.price_per_unit === 'number' && out.price_per_unit > 0) {
            outputMap[key].price_per_unit = out.price_per_unit;
          }
        });
      }
    });
    const finalizeInputs = (m) => Object.values(m).map(({ processes, ...r }) => ({
      ...r, processCount: processes.size, processNames: Array.from(processes),
    }));
    return { inputs: finalizeInputs(inputMap), outputs: Object.values(outputMap) };
  }, [s.project?.processes, s.project?.impact_dataset]);

  const lciInputs = useMemo(() => {
    if (!activeProcess) return [];
    try {
      if (activeProcess.lci_inputs) {
        return typeof activeProcess.lci_inputs === 'string'
          ? JSON.parse(activeProcess.lci_inputs)
          : activeProcess.lci_inputs;
      }
      return [];
    } catch { return []; }
  }, [activeProcess]);

  // The "current" template filename (what sub-inventory references expect).
  const currentTemplateFileName = useMemo(() => {
    const raw = s.project?.template_id || s.project?.project_name || 'custom_template';
    const safe = raw.replace(/[/\\:*?"<>|]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').toLowerCase();
    return safe.endsWith('.json') ? safe : `${safe}.json`;
  }, [s.project?.template_id, s.project?.project_name]);

  const useNoisyDb = s.traciDbMode === 'noisy' || s.isDemoBuild;

  // -- Render ------------------------------------------------------------------

  if (s.showStartup || !s.project) {
    return <ProjectStartup onProjectSelected={s.projectSelected} onOpenSaved={s.loadFromIdb} />;
  }

  const processCount = Object.keys(s.project.processes).length;
  const chemistry = s.project.project_info?.chemistry || '—';

  // Everything the focus-mode components need. The store is the state owner; the
  // Workspace/ tree reads this via useWorkspace() instead of prop-drilling. The
  // handle* names are kept so those components were untouched by the refactor.
  const workspaceValue = {
    // state
    project: s.project, activeProcess, selectedProcess: s.selectedProcess,
    analysisResults: s.analysisResults, isAnalyzing: s.isAnalyzing,
    isDemoBuild: s.isDemoBuild, useNoisyDb, lciInputs, mergedInventory, dirty: s.dirty,
    workspaceMode: s.workspaceMode, leftViewMode: s.leftViewMode,
    currentTemplateFileName, showTemplateSaveDialog: s.showTemplateSaveDialog, newTemplateName: s.newTemplateName,
    editingOutputPrice: s.editingOutputPrice, editingSpecName: s.editingSpecName, specNameDraft: s.specNameDraft,
    // setters
    setProject: s.setProject, setSelectedProcess: s.setSelectedProcess, setLeftViewMode: s.setLeftViewMode,
    setTraciDbMode: s.setTraciDbMode, setShowTemplateSaveDialog: s.setShowTemplateSaveDialog,
    setNewTemplateName: s.setNewTemplateName, setEditingOutputPrice: s.setEditingOutputPrice,
    setEditingSpecName: s.setEditingSpecName, setSpecNameDraft: s.setSpecNameDraft,
    // actions (mapped to the handle* names the components already use)
    goToMode: s.goToMode, handleSelectProcess: s.selectProcess, handleAddProcess: s.addProcess,
    handleUpdateProcess: s.updateProcess, handleUpdateProcessFields: s.updateProcessFields,
    handleRenameProcess: s.renameProcess, handleDeleteProcess: s.deleteProcess,
    handleTableProjectUpdate: s.updateProjectTable,
    // file actions surfaced in the sidebar (replacing the old top bar)
    handleNewProject: s.newProject, handleOpenProject: s.openProject,
    handleSaveProject: s.saveProject, handleLoadFromIdb: s.loadFromIdb,
    handleRecreateFromTemplate: s.recreateFromTemplate,
    handleRunLCA: s.runLCA, handleRunTEA: s.runTEA, handleRunCircularity: s.runCircularity, handleRunAll: s.runAll,
    handleExportResults: s.exportResults, handleOverwriteTemplate: s.overwriteTemplate,
    handleSaveNewTemplate: s.saveNewTemplate, handleSaveComparison: s.saveComparison,
  };

  return (
    <div className={`app${copilotDocked ? (copilotOpen ? ' app--dock-open' : ' app--dock-rail') : ''}`}>
      {/* File actions (New / Open / Recent / Save / Export) now live in the left
          sidebar (ModeRail) — the old top bar was retired. */}
      <WorkspaceContext.Provider value={workspaceValue}>
        <WorkspaceShell />
      </WorkspaceContext.Provider>

      {/* Status bar */}
      <div className="status-bar">
        <div className="status-left">
          <span>{s.statusMessage}</span>
        </div>
        <div className="status-right">
          <span>{s.project.project_info.name}</span>
          <span>{processCount} processes</span>
          <span>{chemistry}</span>
          {isTemplateMode(s.project) && <span className="badge-template">Template Mode</span>}
        </div>
      </div>
    </div>
  );
}

export default App;
